import { formatDate } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { catchError, defer, forkJoin, from, map, Observable, of, switchMap, throwError } from 'rxjs';

import { DistributionPreviewData } from '../../shared/models/distribution/distribution.model';
import { AzureBlobService } from '../../shared/services/azure-blob.service';
import { DistributionService } from '../../shared/services/distribution.service';
import { HomeService } from '../../shared/services/home.service';
import { UserInfoService } from '../../shared/services/user-info.service';
import { AppConstant } from '../../shared/utilities/app.constant';
import { cleanDistributionValue } from '../../shared/utilities/distribution-validation.util';
import { buildRoleTitleTree } from '../../shared/utilities/role-title-tree.util';
import { DistributionContact } from '../home/home-new-asset/home-new-asset-distribution-list/distribution-preview/distribution-preview.component';
import { DistributionListState } from '../home/home-new-asset/home-new-asset-distribution-list/home-new-asset-distribution-list.component';
import {
  applyGroupChanges,
  applyNotificationEdit,
  applyUserAdd,
  applyUserEdit,
  applyUsersRemoval,
  DistributionWorkbookError,
  PRIMARY_SHEET_NAME,
  readDistributionWorkbook,
  readNotificationFlag,
  stripUploadTimestamp,
  verifyNotificationEdit,
  verifyUserEdit,
  verifyUsersRemoval,
  WorkbookNotificationEdit,
  WorkbookSourceRow,
  WorkbookUserEdit,
  writeDistributionWorkbook,
} from './distribution-list-workbook';
import { DISTRIBUTION_LIST_GROUPS, EMAIL_BELONGS_TO_OTHER_MESSAGE } from './distribution-list.constants';
import {
  DistributionListApiResponse,
  DistributionListData,
  DistributionListDraft,
  DistributionListFile,
  DistributionListGroup,
  DistributionListGroupRow,
  DistributionListSharedFields,
  DistributionListSource,
  DistributionListUser,
  DistributionListUserEditPatch,
  ExistingUserInfo,
  GraphUserSearchItem,
  GraphUserSuggestion,
  NewDistributionListUser,
  withNotification,
} from './distribution-list.model';
import {
  applyUsersEdit,
  findEmailConflict,
  findLinkedUsers,
  isOtherSystemUser,
  pruneUserGroups,
  removeUsersFromGroup,
  withGroupRows,
} from './distribution-list.rows';

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const FILE_FIELDS = ['email', 'phone', 'title', 'company', 'name'] as const;

/** Group colors for the upload sheets that are not part of the default groups. */
const DISTRIBUTION_TAB_COLORS: Record<string, string> = {
  primary: '#818F99',
  grey: '#C9CFD4',
  gray: '#C9CFD4',
  orange: '#F7A040',
  peach: '#DF7A5A',
  maroon: '#995B5B',
  brown: '#874F11',
  cyan: '#49B2D3',
  pink: '#DD96B9',
  navy: '#6A85CE',
  lime: '#91A62B',
};

const GRAPH_SEARCH_PAGE = 1;
const GRAPH_SEARCH_PAGE_SIZE = 25;
const GRAPH_USER_COMPANY = 'Halliburton';

/** Part of the `GET user/:id` profile used to resolve the Title. */
interface ExistingUserProfile {
  role?: string | null;
  discipline?: { disciplineName?: string | null } | null;
  phone?: string | null;
}
/** The list that was labelled Primary is displayed as Master. */
const MASTER_GROUP_NAME = 'PRIMARY';

type UpdateUsersResponse = DistributionListApiResponse<{
  userIds: string[];
  patch: DistributionListUserEditPatch;
}>;

export class DistributionRequestError extends Error {
  constructor(readonly requestError: unknown) {
    super('The distribution list file request failed.');
    this.name = 'DistributionRequestError';
  }
}

/**
 * API layer for the Distribution List page.
 *
 * Every change made on the page (add, edit, delete, notification toggle, lists) is written into
 * the `.xlsx` in place with SheetJS when the page has a file:
 * - wizard draft (`DistributionListDraft`): the draft file is edited in memory, no HTTP call; the
 *   wizard uploads it on its own Save;
 * - saved file (`DistributionListSource`): the file is downloaded, edited, verified and re-uploaded
 *   via `PUT rig-journal/edit/:rigJournalId`.
 * Local state changes only after the file was written. Without a file, changes stay in memory.
 */
@Injectable({ providedIn: 'root' })
export class DistributionListService {
  private readonly azureBlobService = inject(AzureBlobService);
  private readonly homeService = inject(HomeService);
  private readonly distributionService = inject(DistributionService);
  private readonly userInfoService = inject(UserInfoService);

  private nextUserId = 1000;
  private currentData: DistributionListData | null = null;
  private source: DistributionListSource | null = null;
  private draft: DistributionListDraft | null = null;
  /** Key of the wizard whose draft file was deleted on the page; consumed by `takeDeletedDraft`. */
  private deletedDraftKey: string | null = null;

  setDistributionList(data: DistributionListData, source: DistributionListSource | null = null): void {
    this.currentData = data;
    this.source = source;
    this.draft = null;
    this.deletedDraftKey = null;
  }

  getCurrentDistributionList(): DistributionListData | null {
    return this.currentData;
  }

  getDistributionListSource(): DistributionListSource | null {
    return this.source;
  }

  /** `draft` is the wizard's current file; edits are then written into it instead of being uploaded. */
  loadFromState(state: DistributionListState, draft: DistributionListDraft | null = null): void {
    this.currentData = this.fromState(state);
    this.source = null;
    this.draft = draft;
    this.deletedDraftKey = null;
  }

  /** Hands the draft back to the wizard identified by `key` (if it owns it) and forgets it. */
  takeDraft(key: string): DistributionListDraft | null {
    const draft = this.draft?.key === key ? this.draft : null;
    this.draft = null;
    return draft;
  }

  /**
   * True (once) when the wizard identified by `key` had its draft file deleted on the page, so it
   * must clear its own file too.
   */
  takeDeletedDraft(key: string): boolean {
    const deleted = this.deletedDraftKey === key;
    if (deleted) {
      this.deletedDraftKey = null;
    }
    return deleted;
  }

  clearDistributionList(): void {
    this.currentData = null;
    this.source = null;
    this.draft = null;
    this.deletedDraftKey = null;
  }

  /** Returns the loaded distribution list, or an empty one when nothing was loaded. */
  getDistributionList(): Observable<DistributionListApiResponse<DistributionListData>> {
    const data = this.currentData ?? { file: this.toFile(), groups: [], users: [] };
    return of({ data });
  }

  /**
   * Searches Microsoft Graph users (first page only) through the same API as
   * the Team Management onboarding page.
   */
  searchGraphUsers(
    keyword: string
  ): Observable<DistributionListApiResponse<GraphUserSuggestion[]>> {
    const term = keyword.trim();
    if (!term) {
      return of({ data: [] });
    }
    return this.userInfoService
      .searchUsersFromGraph(term, GRAPH_SEARCH_PAGE, GRAPH_SEARCH_PAGE_SIZE)
      .pipe(
        map(response => {
          const items: GraphUserSearchItem[] = response?.data ?? [];
          return { data: items.map(item => this.toGraphSuggestion(item)) };
        })
      );
  }

  /**
   * Title and phone of a user that already has a profile (Graph user id = profile id). Title is the
   * Engineer discipline as `Engineer (<discipline>)`, otherwise the role. Values are empty when the
   * profile cannot be loaded.
   */
  getExistingUserProfile(userId: string): Observable<ExistingUserInfo> {
    const empty: ExistingUserInfo = { title: '', phone: '' };
    if (!userId) {
      return of(empty);
    }
    return (this.userInfoService.getUserInfoById(userId) as Observable<unknown>).pipe(
      map(response => {
        const data = (response as { data?: ExistingUserProfile } | null)?.data;
        const role = data?.role?.trim() ?? '';
        const discipline = data?.discipline?.disciplineName?.trim() ?? '';
        const title =
          role === AppConstant.ROLES.ENGINEER.label && discipline ? `${role} (${discipline})` : role;
        return { title, phone: data?.phone?.trim() ?? '' };
      }),
      catchError(() => of(empty))
    );
  }

  /**
   * Title options of the user panel. Uses the same role / discipline configuration as Settings &
   * Team Management (Engineer has one child per discipline); falls back to the app constants.
   */
  getTitleOptions(): Observable<TreeNode[]> {
    return forkJoin({
      roles: this.userInfoService.getRoleList().pipe(catchError(() => of(null))),
      disciplines: this.userInfoService.getDiscipline().pipe(catchError(() => of(null))),
    }).pipe(map(({ roles, disciplines }) => buildRoleTitleTree(roles, disciplines)));
  }

  /**
   * Adds a user. A user typed in manually (no HAL ID from the directory) whose email is a system
   * user under another name belongs to another user and is rejected before anything is written.
   */
  addUser(
    user: NewDistributionListUser
  ): Observable<DistributionListApiResponse<DistributionListUser>> {
    const systemUser$ = user.halId || !user.email.trim() ? of(undefined) : this.findSystemUser(user.email);
    return systemUser$.pipe(
      switchMap(systemUser =>
        isOtherSystemUser(systemUser, user.name)
          ? throwError(
              () => new DistributionWorkbookError('DUPLICATE_EMAIL', EMAIL_BELONGS_TO_OTHER_MESSAGE)
            )
          : this.saveNewUser(user)
      )
    );
  }

  private saveNewUser(
    user: NewDistributionListUser
  ): Observable<DistributionListApiResponse<DistributionListUser>> {
    const data = this.currentData;
    const apply = (): DistributionListApiResponse<DistributionListUser> => {
      const current = this.currentData;
      const newUser = withGroupRows(
        { ...user, id: `user-${this.nextUserId++}` },
        current?.groups ?? [],
        current?.users ?? []
      );
      if (current) {
        this.currentData = { ...current, users: [...current.users, newUser] };
      }
      return { data: newUser };
    };
    if (!this.hasFile() || !data) {
      return of(apply());
    }

    const listSheetNames = this.listSheetNames(data);
    const targetSheetNames = this.sheetNamesOf(data, user.groupIds);
    return this.writeFile((buffer, fileName) => {
      const workbook = readDistributionWorkbook(buffer);
      applyUserAdd(workbook, { user, listSheetNames, targetSheetNames });
      const output = writeDistributionWorkbook(workbook);
      verifyUserEdit(output, { email: user.email, listSheetNames, expectedSheetNames: targetSheetNames });
      return this.toXlsxFile(output, fileName);
    }).pipe(map(apply));
  }

  /**
   * Saves a user patch (single or bulk edit, or "add an existing user to more lists").
   * File fields (email, phone, title, company, name, lists) are written to every row of each user;
   * `patch.originalEmail` locates the rows of a single edit and is never stored on the user.
   * For a single edit, `displayed` (Name and Phone of the form) triggers `resolveSharedFields`; the
   * response carries the resolved patch, which the caller must apply to its own state.
   */
  updateUsers(
    userIds: string[],
    patch: DistributionListUserEditPatch,
    displayed?: DistributionListSharedFields
  ): Observable<UpdateUsersResponse> {
    if (!displayed || userIds.length !== 1) {
      return this.saveUserPatch(userIds, patch);
    }
    return this.resolveSharedFields(userIds[0], patch, displayed).pipe(
      switchMap(resolved => this.saveUserPatch(userIds, resolved))
    );
  }

  /**
   * The Microsoft Graph (system) user whose email equals `email` after trimming and ignoring case,
   * or undefined when there is none or the lookup fails.
   */
  findSystemUser(email: string): Observable<GraphUserSuggestion | undefined> {
    const target = email.trim().toLowerCase();
    return defer(() => this.searchGraphUsers(target)).pipe(
      map(res => res.data.find(item => item.email.trim().toLowerCase() === target)),
      catchError(() => of(undefined))
    );
  }

  /**
   * Resolves the shared Name and Phone once, per field, for every row of the user (matched by email):
   * 1. the Microsoft Graph user with the same normalized email (a failed lookup counts as missing);
   * 2. the value displayed in the edit form (the source row);
   * otherwise the field is left out of the patch so each target row keeps its own value.
   * A Phone typed by the administrator is part of `patch` already and is never replaced.
   * A changed email that is a Graph user with another name belongs to another user and is rejected.
   */
  private resolveSharedFields(
    userId: string,
    patch: DistributionListUserEditPatch,
    displayed: DistributionListSharedFields
  ): Observable<DistributionListUserEditPatch> {
    const user = this.currentData?.users.find(item => item.id === userId);
    const email = (patch.email ?? user?.email ?? '').trim().toLowerCase();
    const graphUser$ = email ? this.findSystemUser(email) : of(undefined);
    return graphUser$.pipe(
      map(graphUser => {
        if (patch.email !== undefined && isOtherSystemUser(graphUser, displayed.name)) {
          throw new DistributionWorkbookError('DUPLICATE_EMAIL', EMAIL_BELONGS_TO_OTHER_MESSAGE);
        }
        const name = cleanDistributionValue(graphUser?.displayName) || displayed.name;
        const phone =
          patch.phone !== undefined
            ? ''
            : cleanDistributionValue(graphUser?.phone) || displayed.phone;
        return { ...patch, ...(name ? { name } : {}), ...(phone ? { phone } : {}) };
      })
    );
  }

  /**
   * The user that already has the email in the sheets checked for this edit: for a changed email the
   * sheets selected under List Added (`patch.groupIds`, or the user's own when unchanged); for an
   * unchanged email only the sheets the user joins. Same rule as the panel.
   */
  private emailConflictOf(
    data: DistributionListData | null,
    userIds: string[],
    patch: DistributionListUserEditPatch
  ): DistributionListUser | undefined {
    const user = data?.users.find(item => item.id === userIds[0]);
    if (!data || !user || userIds.length !== 1) {
      return undefined;
    }
    const groupIds =
      patch.email !== undefined
        ? (patch.groupIds ?? user.groupIds)
        : (patch.groupIds ?? []).filter(id => !user.groupIds.includes(id));
    // A user without email is the same person as the records that hold the new email elsewhere.
    const linkedIds =
      patch.email === undefined
        ? []
        : findLinkedUsers(data.users, user, patch.email).map(item => item.id);
    return findEmailConflict(
      data.users,
      [...userIds, ...linkedIds],
      groupIds,
      patch.email ?? user.email
    );
  }

  private saveUserPatch(
    userIds: string[],
    patch: DistributionListUserEditPatch
  ): Observable<UpdateUsersResponse> {
    const res: UpdateUsersResponse = { data: { userIds, patch } };
    const data = this.currentData;
    const conflict = this.emailConflictOf(data, userIds, patch);
    if (conflict) {
      return throwError(
        () =>
          new DistributionWorkbookError(
            'DUPLICATE_EMAIL',
            `Email "${patch.email}" already exists in the selected distribution list(s).`
          )
      );
    }
    if (!this.hasFile() || !data || !this.touchesFile(patch)) {
      this.applyPatch(userIds, patch);
      return of(res);
    }
    const users = userIds.map(id => data.users.find(item => item.id === id));
    if (users.some(user => !user)) {
      return throwError(() => new Error('The edited user no longer exists in the list.'));
    }
    if (users.length > 1 && (patch.email !== undefined || patch.originalEmail !== undefined)) {
      return throwError(() => new Error('Email can only be changed for one user at a time.'));
    }

    return this.writeFile((buffer, fileName) =>
      this.buildEditedFile(buffer, data, users as DistributionListUser[], patch, fileName)
    ).pipe(
      map(() => {
        this.applyPatch(userIds, patch);
        return res;
      })
    );
  }

  /**
   * Inline "Send Notifications" toggle. Sheet-specific: only the user's row(s) on the sheet of
   * `groupId` change, other sheets keep their own value.
   */
  toggleUserNotification(
    userId: string,
    groupId: string,
    enabled: boolean
  ): Observable<DistributionListApiResponse<DistributionListUser>> {
    const data = this.currentData;
    const user = data?.users.find(item => item.id === userId);
    const group = data?.groups.find(item => item.id === groupId);
    if (!user || !group || !user.groupIds.includes(groupId)) {
      return throwError(() => new Error('The user is no longer in this list.'));
    }
    const apply = (): DistributionListApiResponse<DistributionListUser> => {
      const updated = withNotification(user, groupId, enabled);
      this.replaceUser(updated);
      return { data: updated };
    };
    if (!this.hasFile() || !group.sheetName) {
      return of(apply());
    }

    const edit: WorkbookNotificationEdit = { sheetName: group.sheetName, email: user.email, enabled };
    return this.writeFile((buffer, fileName) => {
      const workbook = readDistributionWorkbook(buffer);
      applyNotificationEdit(workbook, edit);
      const output = writeDistributionWorkbook(workbook);
      verifyNotificationEdit(output, edit);
      return this.toXlsxFile(output, fileName);
    }).pipe(map(apply));
  }

  /** Removes the users from one list only; users left without any list are dropped. */
  deleteUsers(
    userIds: string[],
    groupId: string
  ): Observable<DistributionListApiResponse<string[]>> {
    const data = this.currentData;
    const apply = (): DistributionListApiResponse<string[]> => {
      if (this.currentData) {
        this.currentData = {
          ...this.currentData,
          users: removeUsersFromGroup(this.currentData.users, userIds, groupId),
        };
      }
      return { data: userIds };
    };
    const sheetName = data?.groups.find(group => group.id === groupId)?.sheetName;
    if (!this.hasFile() || !data || !sheetName) {
      return of(apply());
    }

    const removal = {
      sheetName,
      emails: data.users.filter(user => userIds.includes(user.id)).map(user => user.email),
    };
    return this.writeFile((buffer, fileName) => {
      const workbook = readDistributionWorkbook(buffer);
      applyUsersRemoval(workbook, removal);
      const output = writeDistributionWorkbook(workbook);
      verifyUsersRemoval(output, removal);
      return this.toXlsxFile(output, fileName);
    }).pipe(map(apply));
  }

  /** Saves the full list of tabs (create, rename, recolor, delete). */
  saveGroups(
    groups: DistributionListGroup[]
  ): Observable<DistributionListApiResponse<DistributionListGroup[]>> {
    const data = this.currentData;
    const apply = (saved: DistributionListGroup[]): DistributionListApiResponse<DistributionListGroup[]> => {
      if (this.currentData) {
        const groupIds = saved.map(group => group.id);
        this.currentData = {
          ...this.currentData,
          groups: saved,
          users: this.currentData.users.map(user => pruneUserGroups(user, groupIds)),
        };
      }
      return { data: saved };
    };
    if (!this.hasFile() || !data) {
      return of(apply(groups));
    }

    let sheetNames = new Map<string, string>();
    return this.writeFile((buffer, fileName) => {
      const workbook = readDistributionWorkbook(buffer);
      sheetNames = applyGroupChanges(workbook, { before: data.groups, after: groups });
      return this.toXlsxFile(writeDistributionWorkbook(workbook), fileName);
    }).pipe(
      map(() => apply(groups.map(group => ({ ...group, sheetName: sheetNames.get(group.id) }))))
    );
  }

  /** TODO: replace with the real API call. */
  deleteFile(): Observable<DistributionListApiResponse<null>> {
    this.deletedDraftKey = this.draft?.key ?? null;
    this.currentData = null;
    this.source = null;
    this.draft = null;
    return of({ data: null });
  }

  /**
   * Maps a DistributionListState from the upload/preview component
   * to the full DistributionListData page model.
   */
  fromState(state: DistributionListState): DistributionListData {
    const file = this.toFile({
      ...state.metadata,
      name: state.uploadedFile?.name,
      size: state.uploadedFile?.size,
    });

    const defaults = DISTRIBUTION_LIST_GROUPS;
    const groups: DistributionListGroup[] = (state.tabs || []).map((tab, index) => {
      const tabId = (tab.id || '').trim().toLowerCase();
      const known = defaults.find(group => group.id.toLowerCase() === tabId);
      return {
        id: tab.id,
        name: tabId === 'primary' ? MASTER_GROUP_NAME : tab.label,
        color: known?.color ?? DISTRIBUTION_TAB_COLORS[tabId] ?? tab.color ?? '#818F99',
        isDefault: known?.isDefault ?? tab.isPrimary ?? index === 0,
        // The wizard parser uses id "primary" for the Primary sheet and the lower-cased sheet name otherwise.
        sheetName: tab.isPrimary || tabId === 'primary' ? PRIMARY_SHEET_NAME : tab.id,
      };
    });

    const users: DistributionListUser[] = [];
    // Only the same person on a different tab is merged. Two rows of the same
    // tab (or rows without an email) stay separate so the tab keeps every row.
    const usersByEmail = new Map<string, DistributionListUser>();
    let userIdCounter = 1;

    const tabsToProcess = state.tabs?.length
      ? state.tabs
      : [{ id: 'primary', label: 'Primary', count: 0, badgeClass: '' }];

    tabsToProcess.forEach(tab => {
      const contacts =
        state.contactsByTab?.[tab.id] ||
        (tab.id === state.selectedTabId ? state.contacts : []);

      (contacts || []).forEach((contact, position) => {
        const email = cleanDistributionValue(contact.email).toLowerCase();
        const row = this.toGroupRow(contact, position);
        const muted = contact.sendNotification === false ? [tab.id] : [];

        const existing = email ? usersByEmail.get(email) : undefined;
        if (existing && !existing.groupIds.includes(tab.id)) {
          existing.groupIds.push(tab.id);
          existing.mutedGroupIds.push(...muted);
          if (existing.rowsByGroup) {
            existing.rowsByGroup[tab.id] = row;
          }
          return;
        }

        const contactName = cleanDistributionValue(contact.name);
        const isHalliburton =
          contact.company && contact.company !== 'N/A' && contact.company !== 'External';

        const user: DistributionListUser = {
          id: `user-${userIdCounter++}`,
          name: contactName,
          company: isHalliburton
            ? contact.company
            : contact.company && contact.company !== 'N/A'
              ? contact.company
              : 'External',
          title: cleanDistributionValue(contact.title),
          email: email ? contact.email : '',
          phone: cleanDistributionValue(contact.phone),
          mutedGroupIds: muted,
          groupIds: [tab.id],
          ...(contact.halId ? { halId: contact.halId } : {}),
          namesBothBlank: contact.namesBothBlank,
          titleBlank: contact.titleBlank,
          emailBlank: contact.emailBlank,
          phoneBlank: contact.phoneBlank,
          nameHover: contact.nameHover,
          emailHover: contact.emailHover,
          phoneHover: contact.phoneHover,
          validation: contact.validation,
          rowsByGroup: { [tab.id]: row },
        };
        users.push(user);
        if (email && !existing) {
          usersByEmail.set(email, user);
        }
      });
    });

    return { file, groups, users };
  }

  /** `source` is set only when the sheets come from the saved file, enabling write-back. */
  loadFromPreviewSheets(
    sheets: DistributionPreviewData[],
    fileMeta?: Partial<DistributionListFile>,
    source: DistributionListSource | null = null
  ): void {
    const { groups, users } = this.fromPreviewSheets(sheets);
    this.currentData = { file: this.toFile(fileMeta), groups, users };
    this.source = source;
    this.draft = null;
  }

  /**
   * Maps the sheets produced by the existing xlsx upload parser
   * (`DistributionPreviewData[]`) to the page model. Every sheet becomes a
   * group (tab) and users that appear in several sheets are merged by email.
   */
  fromPreviewSheets(
    sheets: DistributionPreviewData[]
  ): Pick<DistributionListData, 'groups' | 'users'> {
    const defaults = DISTRIBUTION_LIST_GROUPS;
    const groups: DistributionListGroup[] = sheets.map((sheet, index) => {
      const id = sheet.sheetName.trim().toLowerCase().replace(/\s+/g, '-');
      const known = defaults.find(group => group.id === id);
      return {
        ...(known ?? {
          id,
          name: sheet.sheetName.trim(),
          color: defaults[index % defaults.length].color,
          isDefault: index === 0,
        }),
        sheetName: sheet.sheetName,
      };
    });

    const usersByEmail = new Map<string, DistributionListUser>();
    sheets.forEach((sheet, index) => {
      const groupId = groups[index].id;
      sheet.data.forEach(record => {
        const email = String(record.EMAIL ?? '')
          .trim()
          .toLowerCase();
        if (!email) {
          return;
        }
        const muted = readNotificationFlag(record) ? [] : [groupId];
        const existing = usersByEmail.get(email);
        if (existing) {
          existing.groupIds.push(groupId);
          existing.mutedGroupIds.push(...muted);
          return;
        }
        const halId = String(record['HAL ID'] ?? '').trim();
        usersByEmail.set(email, {
          id: `xlsx-${usersByEmail.size + 1}`,
          name: `${record['FIRST NAME'] ?? ''} ${record['LAST NAME'] ?? ''}`.trim(),
          company: record.COMPANY ?? (halId ? 'Halliburton' : 'External'),
          title: record.TITLE ?? '',
          email,
          phone: record.PHONE ?? '',
          mutedGroupIds: muted,
          groupIds: [groupId],
          ...(halId ? { halId } : {}),
        });
      });
    });

    return { groups, users: [...usersByEmail.values()] };
  }

  private toGraphSuggestion(item: GraphUserSearchItem): GraphUserSuggestion {
    const email = item.mail || item.userPrincipalName || '';
    return {
      id: item.id || item.halId || email,
      displayName: item.displayName ?? '',
      email,
      company: GRAPH_USER_COMPANY,
      title: item.jobTitle ?? '',
      phone: item.phone ?? '',
      halId: item.halId ?? '',
      existingInSystem: !!item.existingInSystem,
    };
  }

  /** Copies the tab specific data of a preview row, keeping the preview values as they are. */
  private toGroupRow(contact: DistributionContact, position: number): DistributionListGroupRow {
    // `selected` and `sendNotification` are not row data; the remaining fields are copied unchanged.
    const fields: DistributionContact = { ...contact };
    delete fields.selected;
    delete fields.sendNotification;
    return {
      ...fields,
      validation: contact.validation
        ? {
            mandatory: { ...contact.validation.mandatory },
            optional: { ...contact.validation.optional },
          }
        : undefined,
      position,
    };
  }

  /** File card data; the backend `Date.now()_` prefixes are stripped from the name. */
  private toFile(meta: Partial<DistributionListFile> = {}): DistributionListFile {
    return {
      name: stripUploadTimestamp(meta.name || 'Distribution List.xlsx'),
      size: meta.size || '0 B',
      rig: meta.rig || 'N/A',
      customer: meta.customer || 'N/A',
      project: meta.project || 'N/A',
      country: meta.country || 'N/A',
      lastUpdated: meta.lastUpdated || '',
      updatedBy: meta.updatedBy || 'N/A',
    };
  }

  private hasFile(): boolean {
    return !!this.source || !!this.draft;
  }

  private touchesFile(patch: DistributionListUserEditPatch): boolean {
    return [...FILE_FIELDS, 'groupIds' as const].some(key => patch[key] !== undefined);
  }

  private listSheetNames(data: DistributionListData): string[] {
    return data.groups.filter(group => group.sheetName).map(group => group.sheetName!);
  }

  private sheetNamesOf(data: DistributionListData, groupIds: string[]): string[] {
    return groupIds.map(id => data.groups.find(group => group.id === id)?.sheetName ?? id);
  }

  private applyPatch(userIds: string[], patch: DistributionListUserEditPatch): void {
    if (!this.currentData) {
      return;
    }
    const { groups, users } = this.currentData;
    this.currentData = {
      ...this.currentData,
      users: applyUsersEdit(users, groups, userIds, patch),
    };
  }

  private replaceUser(updated: DistributionListUser): void {
    if (!this.currentData) {
      return;
    }
    this.currentData = {
      ...this.currentData,
      users: this.currentData.users.map(user => (user.id === updated.id ? updated : user)),
    };
  }

  /**
   * Edits the wizard draft in memory, or downloads, edits and re-uploads the saved file.
   * `build` receives the current bytes and file name and returns the verified new file.
   * A failed request is rethrown as `DistributionRequestError`.
   */
  private writeFile(build: (buffer: ArrayBuffer, fileName: string) => File): Observable<void> {
    const draft = this.draft;
    if (draft) {
      return from(draft.file.arrayBuffer()).pipe(
        map(buffer => {
          const file = build(buffer, draft.file.name);
          this.draft = { ...draft, file, isNewFile: true };
          this.updateFileMeta(file);
        })
      );
    }
    const source = this.source!;
    const fileName = this.currentData?.file.name ?? '';
    return this.asRequest(this.azureBlobService.getFileByUrl(source.distributionUrl)).pipe(
      switchMap((blob: Blob) => from(blob.arrayBuffer())),
      map(buffer => build(buffer, fileName)),
      switchMap(file =>
        this.asRequest(
          this.homeService.addDistributionList(file, source.rigJournalId, source.language)
        ).pipe(map(response => ({ file, url: response?.data?.distributionListUrl })))
      ),
      map(({ file, url }) => this.onFileSaved(source, file, url))
    );
  }

  private asRequest<T>(request: Observable<T>): Observable<T> {
    return request.pipe(catchError(error => throwError(() => new DistributionRequestError(error))));
  }

  /** Edits every row of the given users and returns the verified file. Throws on any mismatch. */
  private buildEditedFile(
    buffer: ArrayBuffer,
    data: DistributionListData,
    users: DistributionListUser[],
    patch: DistributionListUserEditPatch,
    fileName: string | undefined
  ): File {
    const listSheetNames = this.listSheetNames(data);
    const targetSheetNames = patch.groupIds && this.sheetNamesOf(data, patch.groupIds);
    const changes: WorkbookUserEdit['changes'] = {};
    FILE_FIELDS.forEach(field => {
      if (patch[field] !== undefined) {
        changes[field] = patch[field];
      }
    });

    const workbook = readDistributionWorkbook(buffer);
    const results = users.map(user => {
      const { memberOf, otherRowSheets } = applyUserEdit(workbook, {
        lookupEmail: patch.originalEmail ?? user.email,
        sourceRow: this.sourceRowOf(data, user),
        changes,
        listSheetNames,
        targetSheetNames,
      });
      return { user, expectedSheetNames: [...memberOf, ...(otherRowSheets ?? [])] };
    });
    const output = writeDistributionWorkbook(workbook);
    results.forEach(({ user, expectedSheetNames }) =>
      verifyUserEdit(output, {
        email: patch.email ?? user.email,
        originalEmail: patch.originalEmail,
        listSheetNames,
        expectedSheetNames,
      })
    );
    return this.toXlsxFile(output, fileName);
  }

  /**
   * The row of a user that is on one sheet only, as the page shows it. Without a usable email this
   * is how the workbook finds the row; a user on several sheets has an email, so it has no source row.
   */
  private sourceRowOf(data: DistributionListData, user: DistributionListUser): WorkbookSourceRow | undefined {
    const [groupId] = user.groupIds;
    const sheetName = data.groups.find(group => group.id === groupId)?.sheetName;
    if (user.groupIds.length !== 1 || !sheetName) {
      return undefined;
    }
    const row = user.rowsByGroup?.[groupId];
    return {
      sheetName,
      halId: row?.halId ?? user.halId,
      name: row?.name ?? user.name,
      title: row?.title ?? user.title,
      phone: row?.phone ?? user.phone,
    };
  }

  private toXlsxFile(output: ArrayBuffer, fileName: string | undefined): File {
    const name = stripUploadTimestamp(fileName || 'Distribution List.xlsx');
    return new File([output], /\.xlsx$/i.test(name) ? name : `${name}.xlsx`, {
      type: XLSX_MIME_TYPE,
    });
  }

  private onFileSaved(source: DistributionListSource, file: File, url?: string): void {
    if (url) {
      this.source = { ...source, distributionUrl: url };
    }
    this.updateFileMeta(file);
    this.distributionService.emitDistributionSaved({ rigJournalId: source.rigJournalId });
  }

  private updateFileMeta(file: File): void {
    if (!this.currentData) {
      return;
    }
    const currentUser = this.userInfoService.getUserInfoSubject();
    this.currentData = {
      ...this.currentData,
      file: {
        ...this.currentData.file,
        name: file.name,
        size: `${file.size} B`,
        lastUpdated: formatDate(new Date(), 'MM/dd/yyyy HH:mm', 'en-US'),
        updatedBy: currentUser?.email || currentUser?.name || this.currentData.file.updatedBy,
      },
    };
  }
}
